/**
 * useCandidateAvailabilityEmailFiller.ts — fills availability-request email templates with token data.
 *
 * Why: Availability email preview must wait for all placeholder GraphQL queries before it treats tokens
 * as final. Filling while queries are in flight leaves raw {{TOKENS}} in state and blocks send (BUG-1017).
 */
import { useCallback, useMemo } from 'react';

import { getLocalTimezone } from '@modernloop/shared/datetime';

import { CandidateDetailsQuery, RenderType } from 'src/generated/mloop-graphql';

import ApplicationPlaceholderFiller, {
  useApplicationPlaceholderFillerOptions,
} from 'src/utils/PlaceholderFiller/ApplicationPlaceholderFiller';
import CandidateAvailabilityPlaceholderFiller, {
  useCandidateAvailabilityPlaceholderFillerOptions,
} from 'src/utils/PlaceholderFiller/CandidateAvailabilityPlaceholderFiller';
import CandidatePlaceholderFiller, {
  useCandidatePlaceholderFillerOptions,
} from 'src/utils/PlaceholderFiller/CandidatePlaceholderFiller';
import JobPlaceholderFiller, { useJobPlaceholderFiller } from 'src/utils/PlaceholderFiller/JobPlaceholderFiller';
import TaskPlaceholderFiller, { useTaskPlaceholderFiller } from 'src/utils/PlaceholderFiller/TaskPlaceholderFiller';
import UserPlaceholderFiller, {
  useUserPlaceholderFillerOptions,
} from 'src/utils/PlaceholderFiller/UserPlaceholderFiller';

export type CandidateAvailabilityEmailFillerResult = {
  /** Fills template text with the latest resolved placeholder values. */
  fillText: (input: string) => string;
  /** True while any placeholder query for this email is still loading. */
  loading: boolean;
};

export const useCandidateAvailabilityEmailFiller = (
  applicationId: string,
  jobStageId: string,
  candidateDetails?: CandidateDetailsQuery,
  taskId?: string,
  assigneeId?: string
): CandidateAvailabilityEmailFillerResult => {
  const jobId = candidateDetails?.candidate?.applications?.find((application) => application?.id === applicationId)?.job
    ?.id;
  const candidateId = candidateDetails?.candidate?.id;

  const [applicationPlaceholderFillerOptions, { loading: applicationPlaceholderLoading }] =
    useApplicationPlaceholderFillerOptions({ applicationId });

  const [candidatePlaceholderFillerOptions, { loading: candidatePlaceholderLoading }] =
    useCandidatePlaceholderFillerOptions({
      applicationId,
      candidateId,
      jobId: candidateDetails?.candidate?.applications?.find((application) => application?.id === applicationId)?.job
        ?.id,
    });

  const [candidateAvailabilityPlaceholderFillerOptions, { loading: candidateAvailabilityPlaceholderLoading }] =
    useCandidateAvailabilityPlaceholderFillerOptions({
      applicationId,
      timezone: candidateDetails?.candidate?.timezone || getLocalTimezone(),
    });

  const [userPlaceholderFillerOptions, { loading: userPlaceholderLoading }] = useUserPlaceholderFillerOptions({
    renderType: RenderType.Html,
  });

  const [taskPlaceholderFillerOptions, { loading: taskPlaceholderLoading }] = useTaskPlaceholderFiller({
    renderType: RenderType.Html,
    taskId,
    assigneeId,
  });

  const [jobPlaceholderFillerOptions, { loading: jobPlaceholderLoading }] = useJobPlaceholderFiller({
    renderType: RenderType.Html,
    jobId,
    jobStageId,
    applicationId,
  });

  const loading =
    applicationPlaceholderLoading ||
    candidatePlaceholderLoading ||
    candidateAvailabilityPlaceholderLoading ||
    userPlaceholderLoading ||
    taskPlaceholderLoading ||
    jobPlaceholderLoading ||
    !candidateId;

  const fillText = useCallback(
    (input: string) => {
      let filledResult = ApplicationPlaceholderFiller.getFilledText(input, applicationPlaceholderFillerOptions);
      filledResult = CandidatePlaceholderFiller.getFilledText(filledResult, candidatePlaceholderFillerOptions);
      filledResult = CandidateAvailabilityPlaceholderFiller.getFilledText(
        filledResult,
        candidateAvailabilityPlaceholderFillerOptions
      );
      filledResult = TaskPlaceholderFiller.getFilledText(filledResult, taskPlaceholderFillerOptions);
      filledResult = JobPlaceholderFiller.getFilledText(filledResult, jobPlaceholderFillerOptions);
      filledResult = UserPlaceholderFiller.getFilledText(filledResult, userPlaceholderFillerOptions);
      return filledResult;
    },
    [
      applicationPlaceholderFillerOptions,
      candidatePlaceholderFillerOptions,
      candidateAvailabilityPlaceholderFillerOptions,
      taskPlaceholderFillerOptions,
      jobPlaceholderFillerOptions,
      userPlaceholderFillerOptions,
    ]
  );

  return useMemo(
    () => ({
      fillText,
      loading,
    }),
    [fillText, loading]
  );
};
